import type {LocalizationService} from 'shared/providers';
import type {UrlStateManagerService} from 'shared/state-manager';
import type {ModalDialogService} from 'shared/modal-dialogs';
import type {ToastMessageService} from 'shared/components/kp-toast-messages';
import type {Category, Language, Localization, Translation} from 'src/typings';
import type {ConvertedLocalization, LocalizationsSearchUrlParams} from './types';
import type LocalizationsService from './localizations.service';

export class KpLocalizationsService {
    public static serviceName = 'kpLocalizationsService';

    /*@ngInject*/
    constructor(private localizationsService: LocalizationsService,
                private urlStateManagerService: UrlStateManagerService<LocalizationsSearchUrlParams>,
                private modalDialogService: ModalDialogService,
                private toastMessageService: ToastMessageService,
                private localizationService: LocalizationService) {
    }

    public loadCategoriesAndLanguages(): Promise<[categories: Category[], languages: Language[]]> {
        return Promise.all([
            this.localizationsService.getCategories(),
            this.localizationsService.getLanguages()
        ]);
    }

    public getUrlState$() {
        return this.urlStateManagerService.getState$();
    }

    public showCategory(category: Category) {
        this.urlStateManagerService.requestChangeState({category: category.id});
    }

    public search(query: string | null) {
        this.urlStateManagerService.requestChangeState({q: query});
    }

    public queryBySearchParams(params: LocalizationsSearchUrlParams): Promise<Localization[]> {
        return Object.keys(params).length === 0 ? Promise.resolve([] as Localization[]) : this.localizationsService.query(params);
    }

    public getTranslationsByLanguages(message: Localization, languages: Language[]): Translation[] {
        return languages.map((lang) => {
            return message.translations[lang.id];
        });
    }

    public getCategoryFromMessageCode(messageCode: string): string {
        return messageCode.split('.', 1)[0];
    }

    public createNewMessage(): Promise<Localization> {
        return this.localizationsService.createNewMessage({});
    }

    public editMessage(message: Localization): Promise<Localization> {
        return this.localizationsService.editMessage({
            ...this.convertLocalizationForServer(message),
            confirmed: false
        });
    }

    public async deleteMessage(message: Localization): Promise<void> {
        await this.modalDialogService.openModalWindow('confirmationDialog', {
            text: this.localizationService.get(/* @kp-localization localization.DeleteKeyConfirmation */ 'localization.DeleteKeyConfirmation')
        });

        await this.localizationsService.deleteMessage(message);

        this.toastMessageService.showSuccess(
            this.localizationService.get(/* @kp-localization localization.KeyDeleted */ 'localization.KeyDeleted')
        );
    }

    private convertLocalizationForServer(localization: Localization): ConvertedLocalization {
        const convertedLocalization: ConvertedLocalization = {
            code: localization.code,
            department: localization.depId
        }

        Object.keys(localization.translations).forEach((key) => convertedLocalization[key] = localization.translations[key]?.value);

        return convertedLocalization;
    }
}